import moment from "moment";
import { InvoiceApi, InvoiceDataType } from "@/api/InvoiceApi";
import { CategoryApi } from "@/api/CategoryApi";
import { InvoiceProductDataType } from "@/api/InvoiceProductApi";
import { ReportDataProps, ReportTitleProps } from "@components/dynamic/ReportPage";
import { formatCant, roundCant } from "@utils/Cant";
import { CURRENCIES_CODE_IDS } from "@utils/Constants";
import { isValidEDocument } from "@utils/EDocument";
import { formatReportAmount } from "@utils/ProductsByCategoryReport";

/** Valor del select "Todos los clientes" en el formulario del reporte. */
export const ALL_CLIENTS_OPTION = "-2";

/** Valor del select "Todos los vendedores" en el formulario del reporte. */
export const ALL_SALEMEN_OPTION = "-2";

/** Clave y etiqueta del grupo que recoge las lineas sin producto o sin categoria. */
export const NO_CATEGORY_KEY = "sin-categoria";

export const NO_CATEGORY_LABEL = "Sin categoria";

export const TOTAL_ROW_LABEL = "TOTALES";

export type SalesByClientReportParams = {
    clientId?: string,
    salemanId?: string,
    start: string,
    end: string,
    groupByCategory?: boolean
}

export type SalesByClientReportRow = {
    /** productId, o categoryId cuando el reporte agrupa por categoria. */
    key: string,
    codigo: string,
    codigo_cabys: string,
    /** Descripcion del producto, o nombre de la categoria al agrupar. */
    description: string,
    totalCant: number,
    /** Venta neta (subtotal - descuento) convertida a colones. */
    totalAmount: number
}

export type SalesByClientReportResult = {
    rows: SalesByClientReportRow[],
    totalCant: number,
    totalAmount: number
}

/** El select usa "-2" para "todos los clientes"; la API espera cadena vacia. */
export const resolveReportClientId = (clientId?: string | null): string => {
    const value = `${clientId || ""}`;
    return value === ALL_CLIENTS_OPTION ? "" : value;
}

/** El select usa "-2" para "todos los vendedores"; la API espera cadena vacia. */
export const resolveReportSalemanId = (salemanId?: string | null): string => {
    const value = `${salemanId || ""}`;
    return value === ALL_SALEMEN_OPTION ? "" : value;
}

const roundAmount = (value: number): number => {
    return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
}

/**
 * Factor para llevar los montos de la factura a colones. Solo se aplica cuando la
 * moneda no es CRC: asi el `exchange_rate` guardado en una factura en colones nunca
 * distorsiona el total.
 */
const resolveExchangeRate = (invoice: InvoiceDataType): number => {
    const currencyId = Number(invoice.currency?.currency_id ?? CURRENCIES_CODE_IDS.CRC);
    if (currencyId === CURRENCIES_CODE_IDS.CRC) {
        return 1;
    }
    return Number(invoice.exchange_rate || 1);
}

/** Las anuladas y las rechazadas por Hacienda no son venta y no deben sumar. */
const isCountableInvoice = (invoice: InvoiceDataType): boolean => {
    return !invoice.anulada && isValidEDocument(`${invoice.edocument_status_payload || ""}`);
}

type ProductWithCategory = InvoiceProductDataType & {
    product?: { categoryId?: string | null } | null
}

/**
 * Consulta las facturas del cliente y/o del vendedor en el periodo y agrupa las lineas
 * por producto (o por categoria del producto) sumando cantidades y venta neta en
 * colones. Es la unica fuente de datos del reporte: la usan tanto la vista en pantalla
 * como la version impresa/Excel.
 */
export const fetchSalesByClientProducts = async (params: SalesByClientReportParams): Promise<SalesByClientReportResult> => {
    const { clientId, salemanId, start, end, groupByCategory = false } = params;

    const startDateRange = moment(start).utc().toISOString();
    const endDateRange = moment(end).add(1, "day").utc().toISOString();

    const reportClientId = resolveReportClientId(clientId);
    const reportSalemanId = resolveReportSalemanId(salemanId);

    const rowsByKey: Record<string, SalesByClientReportRow> = {};
    let nextToken: string | undefined = undefined;

    do {
        const { items, nextToken: nt } = await InvoiceApi.fetchAllByClientPeriod(
            reportClientId,
            startDateRange,
            endDateRange,
            {},
            nextToken,
            1000,
            "DESC",
            groupByCategory,
            reportSalemanId
        );
        // El token debe reenviarse: sin esto se vuelve a pedir la primera pagina
        // indefinidamente cuando el periodo supera el limite de la consulta.
        nextToken = nt;

        for (const invoice of items) {
            if (!isCountableInvoice(invoice)) {
                continue;
            }

            const rate = resolveExchangeRate(invoice);

            for (const line of (invoice.invoiceProducts || []) as ProductWithCategory[]) {
                const key = groupByCategory
                    ? `${line.product?.categoryId || NO_CATEGORY_KEY}`
                    : `${line.productId || ""}`;

                if (!key) {
                    continue;
                }

                const amount = (Number(line.subtotal || 0) - Number(line.descuento || 0)) * rate;
                const existingRow = rowsByKey[key];

                rowsByKey[key] = existingRow
                    ? {
                        ...existingRow,
                        totalCant: roundCant(existingRow.totalCant + Number(line.cant || 0)),
                        totalAmount: existingRow.totalAmount + amount
                    }
                    : {
                        key,
                        codigo: `${line.codigo || ""}`,
                        codigo_cabys: `${line.codigo_cabys || ""}`,
                        description: `${line.description || ""}`,
                        totalCant: roundCant(Number(line.cant || 0)),
                        totalAmount: amount
                    };
            }
        }
    } while (nextToken);

    let rows = Object.values(rowsByKey).map(r => ({ ...r, totalAmount: roundAmount(r.totalAmount) }));

    if (groupByCategory) {
        rows = await applyCategoryLabels(rows);
    }

    // Lo que mas se vendio primero: es la lectura util del reporte.
    rows.sort((a, b) => b.totalAmount - a.totalAmount);

    return {
        rows,
        totalCant: roundCant(rows.reduce((a, r) => a + r.totalCant, 0)),
        totalAmount: roundAmount(rows.reduce((a, r) => a + r.totalAmount, 0))
    };
}

/** Una sola consulta de categorias para resolver los nombres de todos los grupos. */
const applyCategoryLabels = async (rows: SalesByClientReportRow[]): Promise<SalesByClientReportRow[]> => {
    const categories = await CategoryApi.optionsList();

    return rows.map(row => ({
        ...row,
        codigo: "",
        codigo_cabys: "",
        description: row.key === NO_CATEGORY_KEY
            ? NO_CATEGORY_LABEL
            : (categories[row.key] || NO_CATEGORY_LABEL)
    }));
}

export const buildSalesByClientTitles = (groupByCategory: boolean): ReportTitleProps[] => {
    if (groupByCategory) {
        return [
            { label: "Categoria", width: "60%" },
            { label: "Cantidad", width: "18%", isNumeric: true },
            { label: "Total", width: "22%", isNumeric: true },
        ];
    }

    return [
        { label: "Codigo", width: "12%" },
        { label: "Codigo Cabys", width: "18%" },
        { label: "Descripcion", width: "40%" },
        { label: "Cantidad", width: "12%", isNumeric: true },
        { label: "Total", width: "18%", isNumeric: true },
    ];
}

/**
 * Arma las filas del reporte y cierra con la fila de totales. La fila de totales va
 * dentro de `reportData` a proposito: el export a Excel de `ReportPage` solo lee
 * `reportData`, asi que de otro modo el total no llegaria a la hoja.
 * `totalRowClassName` lo aporta la pagina de impresion (clase de su modulo SCSS)
 * para no introducir estilos globales.
 */
export const buildSalesByClientRows = (
    result: SalesByClientReportResult,
    groupByCategory: boolean,
    totalRowClassName?: string
): ReportDataProps<SalesByClientReportRow>[] => {
    const { rows, totalCant, totalAmount } = result;

    if (rows.length === 0) {
        return [];
    }

    const dataRows: ReportDataProps<SalesByClientReportRow>[] = rows.map(row => ({
        sourceData: row,
        values: groupByCategory
            ? [row.description, formatCant(row.totalCant), formatReportAmount(row.totalAmount)]
            : [
                row.codigo || "Temporal",
                row.codigo_cabys || "Temporal",
                row.description || "Temporal",
                formatCant(row.totalCant),
                formatReportAmount(row.totalAmount)
            ]
    }));

    const totalRow: ReportDataProps<SalesByClientReportRow> = {
        excludeFromTotal: true,
        className: totalRowClassName,
        values: groupByCategory
            ? [TOTAL_ROW_LABEL, formatCant(totalCant), formatReportAmount(totalAmount)]
            : [TOTAL_ROW_LABEL, "", "", formatCant(totalCant), formatReportAmount(totalAmount)]
    };

    return [...dataRows, totalRow];
}
